import TileGrid from 'ol/tilegrid/TileGrid';
import TileLayer from 'ol/layer/Tile';
import { TileWMS, XYZ } from 'ol/source';
import { Err, Ok, Result } from 'ts-results';
import { InternalDistributionFormat } from '@/services/common/enums';
import { defaultSettings } from '@/services/map-services/map-service';
import VectorSource from 'ol/source/Vector';
import { createXYZ } from 'ol/tilegrid';
import { EsriJSON, MVT } from 'ol/format';
import { tile as tileStrategy } from 'ol/loadingstrategy';
import VectorLayer from 'ol/layer/Vector';
import { createStyleFunction } from '@/assets/map/styles';
import { selectStyle as selectionStyleFunction } from '@/assets/map/selected-styles';
import httpService from '@/services/common/http-service';
import axios from 'axios';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import { Feature } from 'ol';
const esrijsonFormat = new EsriJSON();
// Main functions
export function createSelectionLayer() {
    return new VectorLayer({
        source: new VectorSource({
            features: [],
        }),
        zIndex: 100,
        style: selectionStyleFunction,
        properties: { id: 'selection-layer' },
    });
}
export function createLayer(layerInfo) {
    console.log(layerInfo.web_display_distributions);
    const mvt = tryGetDistribution(layerInfo, InternalDistributionFormat.MVT).andThen((val) => Ok(createMvtLayer(layerInfo, val)));
    const featureServer = tryGetDistribution(layerInfo, InternalDistributionFormat.Feature).andThen((val) => Ok(createFeatureServiceLayer(layerInfo, val)));
    const wms = tryGetDistribution(layerInfo, InternalDistributionFormat.WMS).andThen((val) => Ok(createWMSLayer(layerInfo, val)));
    const wfs = tryGetDistribution(layerInfo, InternalDistributionFormat.WFS).andThen((val) => Ok(createEsriWFSLayer(layerInfo, val)));
    const distributionResult = Result.any(mvt, featureServer, wms, wfs);
    if (distributionResult.ok) {
        return Ok(distributionResult.val);
    }
    return Err(distributionResult.val[0]);
}
// Specific layer format functions
function createWMSLayer(details, distributionToUse) {
    const sridString = `EPSG:${details.extents.spatial.crs}`;
    const requiredParameters = requiredParametersDictionary(details.required_parameters);
    const layerSource = new TileWMS({
        url: process.env.VUE_APP_BASE_URL + '/Layers/' + details.id + '/WMS',
        params: { CRS: sridString, SRS: sridString, LAYERS: distributionToUse.layerIdentifier || 0, ...requiredParameters },
        transition: 0,
        tileGrid: tileGrid,
        projection: `EPSG:${details.extents.spatial.crs}`,
    });
    return createTileLayer(layerSource, details, distributionToUse);
}
function createXYZLayer(details, distributionToUse) {
    return new TileLayer({
        source: new XYZ({
            attributions: 'Copyright:© 2013 ESRI, i-cubed, GeoEye',
            url: 'https://gis.apfo.usda.gov/arcgis/rest/services/NAIP/USDA_CONUS_PRIME/ImageServer/tile/{z}/{y}/{x}',
            maxZoom: 15,
            projection: 'EPSG:3857',
            tileSize: 512,
            maxResolution: 180 / 512,
            wrapX: true,
        }),
    });
}
function createFeatureServiceLayer(details, distributionToUse) {
    const serviceUrl = '/Layers/' + details.id + '/Feature';
    const selectStyle = createStyleFunction(details);
    const featureServiceSource = new VectorSource({
        loader: function (extent, resolution, projection, success, failure) {
            const url = serviceUrl +
                '?f=json&' +
                'returnGeometry=true&spatialRel=esriSpatialRelIntersects&geometry=' +
                encodeURIComponent('{"xmin":' +
                    extent[0] +
                    ',"ymin":' +
                    extent[1] +
                    ',"xmax":' +
                    extent[2] +
                    ',"ymax":' +
                    extent[3] +
                    ',"spatialReference":{"wkid":102100}}') +
                '&geometryType=esriGeometryEnvelope&inSR=102100&outFields=*' +
                '&outSR=102100';
            httpService.get(url).then(({ data }) => {
                try {
                    const features = esrijsonFormat.readFeatures(data, {
                        featureProjection: projection,
                    });
                    if (features.length > 0) {
                        featureServiceSource.addFeatures(features);
                    }
                }
                catch (error) {
                    if (axios.isAxiosError(error)) {
                        console.log('error message: ' + error.message);
                    }
                    console.log('unexpected error: ' + error);
                }
            });
        },
        strategy: tileStrategy(createXYZ({
            tileSize: 512,
        })),
    });
    return new VectorLayer({
        source: featureServiceSource,
        style: selectStyle,
        properties: { ...details, DistributionFormat: distributionToUse.format, DistributionURL: distributionToUse.accessURL },
    });
}
function createEsriWFSLayer(details, distributionToUse) {
    const serviceUrl = process.env.VUE_APP_BASE_URL + '/Layers/' + details.id + '/WFS';
    const vectorSource = new VectorSource({
        format: esrijsonFormat,
        url: function (extent, resolution) {
            return serviceUrl + esriQueryString(details, extent);
        },
        strategy: tileStrategy(createXYZ({
            tileSize: 512,
        })),
    });
    vectorSource.on('featuresloadstart', function () {
        console.log('features loading...');
    });
    vectorSource.on('featuresloadend', function () {
        console.log('features load complete');
    });
    vectorSource.on('featuresloaderror', function () {
        console.log('oops, that didnt go well');
    });
    const selectStyle = createStyleFunction(details);
    return new VectorLayer({
        source: vectorSource,
        style: selectStyle,
        properties: { ...details, DistributionFormat: distributionToUse.format, DistributionURL: distributionToUse.accessURL, loading: false },
    });
}
function createMvtLayer(details, distributionToUse) {
    const selectStyle = createStyleFunction(details);
    return new VectorTileLayer({
        className: details.title,
        declutter: true,
        source: new VectorTileSource({
            format: new MVT({ featureClass: Feature }),
            url: distributionToUse.accessURL + queryStringFormat(requiredParametersDictionary(details.required_parameters)),
        }),
        properties: { ...details, DistributionFormat: distributionToUse.format, DistributionURL: distributionToUse.accessURL },
        style: selectStyle,
        minZoom: details.extents.spatial.minzoom,
        maxZoom: details.extents.spatial.maxzoom,
    });
}
// Helper functions
const tileGrid = new TileGrid({
    extent: defaultSettings.webMercatorExtent,
    resolutions: defaultSettings.resolutions,
    tileSize: [512, 512],
});
function tryGetDistribution(layerInfo, format) {
    const distribution = layerInfo.web_display_distributions.find((x) => x.format === format);
    return distribution === undefined ? Err('No valid distribution found.') : Ok(distribution);
}
function requiredParametersDictionary(requiredParameters) {
    const parameters = {};
    for (const [key, value] of Object.entries(requiredParameters)) {
        // @ts-ignore
        parameters[key] = value.defaultValue;
    }
    return parameters;
}
function serviceUrl(id, format) {
    return `${process.env.VUE_APP_BASE_URL}/Layers/${id}/${format}`;
}
function layerParametersToEsriQuery(parameters) {
    return 'where=1%3D1'; // no filter applied
}
function queryStringFormat(parameterObject) {
    let queryString = '';
    for (const parameterName in parameterObject) {
        if (queryString.length > 0) {
            queryString += '&';
        }
        queryString += `${parameterName}=${parameterObject[parameterName]}`;
    }
    return queryString.length > 0 ? '?' + queryString : queryString;
}
function esriQueryString(details, extent) {
    let queryString = '?' + layerParametersToEsriQuery(null);
    queryString += '&outFields=*';
    if (extent !== null) {
        queryString += `&geometry=${extent[0]}%2C${extent[1]}%2C${extent[2]}%2C${extent[3]}`;
        queryString += '&geometryType=esriGeometryEnvelope&inSR=3857&spatialRel=esriSpatialRelIntersects';
    }
    queryString += '&outSR=3857&f=json';
    return queryString;
}
function createTileLayer(source, details, distribution) {
    return new TileLayer({
        source: source,
        opacity: details.opacity || 1,
        visible: true,
        properties: { ...details, DistributionFormat: distribution.format, DistributionURL: distribution.accessURL },
    });
}
